import {
  ExpensesEntitiesExpensableResourceTypeEnum,
  ExpensesEntitiesExpensableStatusEnum,
  ExpensesEntitiesExpenseCreationTypeEnum,
} from 'generated/resources/graphql'

import { track, trackingEvents } from 'modules/core/lib/Tracking'

import type { Expensable, ExpensablePerDiem } from 'modules/expenses/types/Expensable'

type ExpensableAmplitudeData = Pick<
  Expensable,
  'id' | 'type' | 'status' | 'expense' | 'perDiem'
> & {
  creationType?: ExpensesEntitiesExpenseCreationTypeEnum
  category?: string
  fromDocument?: boolean
}

interface AmplitudeContext {
  context?: string
  previousStatus?: string
  action?: string
  period?: string
  duplicateAlert?: boolean
}

export const AmplitudeExpenseType = {
  Mileage: 'mileage',
  PerDiem: 'per_diem',
  General: 'general',
  Group: 'group',
} as const

type AmplitudeExpenseType = (typeof AmplitudeExpenseType)[keyof typeof AmplitudeExpenseType]

const mapExpensableTypeToAmplitude = (
  expensableType: ExpensesEntitiesExpensableResourceTypeEnum
): AmplitudeExpenseType => {
  switch (expensableType) {
    case 'mileage':
      return AmplitudeExpenseType.Mileage
    case 'perdiem':
      return AmplitudeExpenseType.PerDiem
    default:
      return AmplitudeExpenseType.General
  }
}

// LEGACY data for Amplitude events
const expensableAmplitudeData = (
  expensable: ExpensableAmplitudeData,
  {
    context = '',
    previousStatus = '',
    action = '',
    period = '',
    duplicateAlert = false,
  }: AmplitudeContext
) => {
  const expense_type = mapExpensableTypeToAmplitude(expensable.type)
  const origin = expensable.creationType == 'manual' ? 'factorial' : 'stripe'

  return {
    expense_id: expensable.id,
    expense_type,
    creation_type: expensable.creationType,
    category: expensable.category,
    current_status: expensable.status,
    previous_status: previousStatus,
    ocr: expensable.fromDocument,
    origin,
    context,
    action,
    period,
    duplicate_alert: duplicateAlert,
    ...(expensable.perDiem ? perDiemAmplitudeData(expensable.perDiem) : {}),
  }
}

const perDiemAmplitudeData = (perDiem: ExpensablePerDiem) => {
  if (!perDiem) return {}

  const { currency, from, to, startDate, endDate, perDiemRates } = perDiem
  return {
    currency,
    trip_origin: from,
    trip_destination: to,
    start_date: startDate,
    end_date: endDate,
    per_diem_rates: JSON.stringify(
      perDiemRates.map((rate) => ({
        id: rate.rateConfigurationId,
        units: rate.totalDays,
        amount: rate.totalAmountCents,
      }))
    ),
  }
}

export const trackAddExpensable = (expenseType: AmplitudeExpenseType) => {
  track(trackingEvents.expenses.addNewExpense, { expense_type: expenseType })
}

export const trackCreatedExpensable = (expensable: ExpensableAmplitudeData) => {
  track(
    trackingEvents.expenses.createNewExpense,
    expensableAmplitudeData(expensable, {
      context: 'personal',
    })
  )
}

// LEGACY method for tracking expensable edits in Amplitude, to be removed soon(TM)
export const trackEditedExpensable = (
  expensable: ExpensableAmplitudeData,
  context: AmplitudeContext
) => {
  track(trackingEvents.expenses.editExistingExpense, expensableAmplitudeData(expensable, context))
}

export const trackUploadDocument = (option: 'skip' | 'next') => {
  track(trackingEvents.expenses.uploadDocument, {
    context: 'personal',
    option: option,
  })
}

type SaveExpensableAmplitudeData = {
  expense_id?: number
  expense_type?: AmplitudeExpenseType
  creation_type?: ExpensesEntitiesExpenseCreationTypeEnum
  ocr?: boolean
  origin?: 'factorial' | 'stripe'
  current_status?: ExpensesEntitiesExpensableStatusEnum
  previous_status?: ExpensesEntitiesExpensableStatusEnum
  action?: 'new' | 'edit'
  success?: boolean
  context?: 'company' | 'personal'
  duplicate_alert?: boolean
}

const trackEditExpensableData = (
  expensable: ExpensableAmplitudeData,
  data: SaveExpensableAmplitudeData
): SaveExpensableAmplitudeData => {
  const expense_type = mapExpensableTypeToAmplitude(expensable.type)
  const creation_type = expensable?.expense?.creationType || 'manual'

  return {
    expense_id: expensable.id,
    expense_type,
    creation_type,
    ocr: !!data.ocr,
    origin: creation_type !== 'manual' ? 'stripe' : 'factorial',
    current_status: expensable.status,
    previous_status: data.previous_status,
    context: data.context,
    action: data.action,
    success: data.success,
    duplicate_alert: data.duplicate_alert,
  }
}

export const trackSaveExpensable = (data: SaveExpensableAmplitudeData) => {
  track(trackingEvents.expenses.saveExpense, data)
}

export const trackEditExpensable = (
  expensable: ExpensableAmplitudeData,
  data: SaveExpensableAmplitudeData
) => {
  track(trackingEvents.expenses.saveExpense, trackEditExpensableData(expensable, data))
}

export const trackDuplicateExpense = (expensable: ExpensableAmplitudeData) => {
  track(trackingEvents.expenses.viewDuplicateExpense, {
    expense_id: expensable.id,
    expense_type: expensable.type,
    current_status: expensable.status,
  })
}
